import type { ConversationTurnEntry } from '@/features/events/conversation/state/types'
import type { FileChanges, FileUpdate } from '@/infra/sse/conversation/payloads'
import { EventType } from '@/features/events/shared/types'
import { isExitPlanModeToolInput } from '@/features/events/shared/types/tool-inputs'

export function extractEventContent(event: ConversationTurnEntry): string {
  const toolInput = event.notificationContext?.toolInput as { error?: string } | undefined
  if (toolInput?.error) {
    return toolInput.error
  }

  if (event.eventType === EventType.FILE_MODIFICATION) {
    if (!event.fileChanges) {
      return 'File modification details not available'
    }
    return extractFileModificationContent(event.fileChanges, event.toolName)
  }

  if (event.agentResponse) {
    return event.agentResponse
  }

  if (event.eventType === EventType.AGENT_NARRATIVE && event.agentResponse) {
    return event.agentResponse
  }

  if (
    event.notificationContext?.toolInput &&
    isExitPlanModeToolInput(event.notificationContext.toolInput)
  ) {
    return event.notificationContext.toolInput.plan
  }

  if (event.notificationContext?.agentDialogue?.agentPrompt) {
    return event.notificationContext.agentDialogue.agentPrompt
  }

  if (event.eventType === EventType.USER_PROMPT && event.userPrompt) {
    return event.userPrompt
  }

  if (event.notificationMessage) {
    return event.notificationMessage
  }

  return 'No content available'
}

function extractFileModificationContent(fileChanges: FileChanges, toolName: string | null): string {
  if (toolName === 'Write' && fileChanges.newContent !== null) {
    return fileChanges.newContent
  }

  if (fileChanges.updates && fileChanges.updates.length > 0) {
    return formatEditUpdates(fileChanges.updates)
  }

  if (fileChanges.newContent !== null) {
    return fileChanges.newContent
  }

  return `File: ${fileChanges.targetPath}`
}

function formatEditUpdates(updates: readonly FileUpdate[]): string {
  return updates.map(formatSingleUpdate).join('\n\n')
}

function formatSingleUpdate(update: FileUpdate): string {
  const header = `@@ -${update.oldStart},${update.oldLines} +${update.newStart},${update.newLines} @@`
  const lines = update.lines.join('\n')
  return `${header}\n${lines}`
}
